import {useContext, useEffect, type MouseEvent} from 'react';

import {useTranslation} from 'react-i18next';
import {useLocation, useNavigate, useParams, useSearchParams} from 'react-router-dom';

import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined';
import {Button, Typography} from '@mui/material';

import {EmpionPaper} from '../../../components/EmpionPaper/EmpionPaper';
import {ErrorMessage} from '../../../components/ErrorMessage/ErrorMessage';
import {LoadingOverlay} from '../../../components/Loading/LoadingOverlay';
import {CompanyContext} from '../../../contexts/CompanyContext/CompanyContext';
import {LanguageContext} from '../../../contexts/LanguageContext/LanguageContext';
import {Actions} from '../../../features/company/recruiting/applicants/components/NewMatchDetails/Actions';
import {HeaderInformation} from '../../../features/company/recruiting/applicants/components/NewMatchDetails/HeaderInformation';
import {MatchFit} from '../../../features/company/recruiting/applicants/components/NewMatchDetails/MatchFit/MatchFit';
import {MatchProfileSections} from '../../../features/company/recruiting/applicants/components/NewMatchDetails/MatchProfileSections';
import {MatchSections} from '../../../features/company/recruiting/applicants/components/NewMatchDetails/MatchTabs/MatchSections';
import {RejectionInfoBox} from '../../../features/company/recruiting/applicants/components/NewMatchDetails/RejectionInfoBox';
import {useApiRead} from '../../../hooks/useApiRead';
import {greenColors} from '../../../layouts/theme/designSystem';
import {GetRoutes} from '../../../lib/api/GET/GetRoutes';
import {AppConfig} from '../../../lib/AppConfig';
import {
  MATCH_DETAILS_FROM_MATCHES,
  MATCH_DETAILS_FROM_TALENT_POOL,
  MATCH_DETAILS_RETURN_TO_PARAM,
  type MatchDetailsLocationState,
} from '../../../lib/matchDetailsNav';
import {MatchData} from '../../../types/shared-recruiting-types';

export const MATCH_DETAILS_QUERY_KEY = 'match-details';

export const MatchDetailsPage = () => {
  const urlParams = useParams();
  const matchId = urlParams.id || '';
  const navigate = useNavigate();
  const location = useLocation();
  const {company} = useContext(CompanyContext);
  const companyId = company.data?.resourceId || '';
  const [searchParams, setSearchParams] = useSearchParams();
  const lang = useContext(LanguageContext);
  const {t} = useTranslation();

  const {
    data,
    error: matchError,
    isLoading,
  } = useApiRead<{companyMatch: MatchData}>({
    path: GetRoutes.companyMatchDetails(companyId, matchId),
    options: {
      enabled: !!matchId && !!companyId,
      // Scope cache per match so navigating between details never shows another match's data while refetching.
      queryKey: [MATCH_DETAILS_QUERY_KEY, companyId, matchId],
    },
  });

  useEffect(() => {
    const urlLang = searchParams.get('lang');

    if (urlLang !== lang) {
      setSearchParams(
        (prev) => {
          prev.set('lang', lang);
          return prev;
        },
        // Preserve router state (e.g. matchDetailsReturnPath from Talent Pool); replace alone drops it.
        {replace: true, state: location.state}
      );
    }
  }, [lang, searchParams, setSearchParams, location.state]);

  if (isLoading) {
    return <LoadingOverlay />;
  }

  if (matchError) {
    return <ErrorMessage />;
  }

  if (!data?.companyMatch) {
    return null;
  }

  const navState = location.state as MatchDetailsLocationState | null;
  const returnTo = searchParams.get(MATCH_DETAILS_RETURN_TO_PARAM);

  const returnPath =
    returnTo === MATCH_DETAILS_FROM_TALENT_POOL
      ? AppConfig.COMPANY_ADMIN_RECRUITING_TALENT_POOL_PATH
      : returnTo === MATCH_DETAILS_FROM_MATCHES
        ? AppConfig.COMPANY_ADMIN_RECRUITING_MATCHES_PATH
        : navState?.matchDetailsReturnPath ?? AppConfig.COMPANY_ADMIN_RECRUITING_MATCHES_PATH;

  const backLabel =
    returnPath === AppConfig.COMPANY_ADMIN_RECRUITING_TALENT_POOL_PATH
      ? t('appHeader.menuLabels.companyAdmin.recruiting.talentPool')
      : t('appHeader.menuLabels.companyAdmin.recruiting.matches');

  const onClick = (e: MouseEvent) => {
    e.stopPropagation();
    if (returnTo === MATCH_DETAILS_FROM_TALENT_POOL || returnTo === MATCH_DETAILS_FROM_MATCHES) {
      const q = new URLSearchParams(searchParams);
      q.delete(MATCH_DETAILS_RETURN_TO_PARAM);
      const s = q.toString();
      navigate(s ? `${returnPath}?${s}` : returnPath);
      return;
    }
    const returnSearch = navState?.matchDetailsReturnSearch;
    navigate(returnSearch ? `${returnPath}${returnSearch}` : returnPath);
  };

  return (
    <EmpionPaper
      id='match-details-view'
      sx={{
        padding: 4,
        borderRadius: '16px',
      }}
    >
      <Button
        onClick={onClick}
        style={{display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '24px', color: greenColors[900]}}
      >
        <ArrowBackOutlinedIcon />
        <Typography variant='body1'>{backLabel}</Typography>
      </Button>
      <HeaderInformation match={data?.companyMatch} />
      <Actions match={data?.companyMatch} />
      <RejectionInfoBox match={data?.companyMatch} />
      <MatchFit match={data?.companyMatch} layout='row' />
      <MatchProfileSections match={data?.companyMatch} />
      <MatchSections match={data?.companyMatch} />
    </EmpionPaper>
  );
};
